import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useBlocker } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AlertTriangle, BarChart3, ClipboardCheck, History, Loader2, Save, Search, UserCheck, UserX } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AttendanceStatsDialog } from "@/components/attendance/AttendanceStatsDialog";
import {
  listSchedulesForAttendance,
  getAttendance,
  saveAttendance,
  listStudentsForAttendance,
  getStudentAttendanceHistory,
} from "@/lib/api/attendance.functions";
import { ChangeClassButton } from "@/components/students/ChangeClassButton";
import { brToday } from "@/lib/tz";

export const Route = createFileRoute("/_authenticated/m/lista-chamada")({
  component: ListaChamadaPage,
});

const DAYS = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];

type ScheduleRow = {
  id: string;
  name: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  category: string | null;
  location?: string | null;
  rental_modalities?: { name: string } | null;
};

type Row = {
  id: string;
  name: string;
  jersey_number: string | null;
  photo_url: string | null;
  present: boolean | null;
  notes: string | null;
  marked_at: string | null;
};

type HistoryEntry = {
  class_date: string;
  present: boolean;
  notes: string | null;
  marked_at: string | null;
  schedule_name: string;
  schedule_category: string | null;
  start_time: string | null;
  end_time: string | null;
};

const fmtTime = (t: string) => (t ? t.slice(0, 5) : "");
const DAYS_SHORT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

const fmtDateTime = (iso: string | null) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const fmtDateBR = (ymd: string) => {
  const [y, m, d] = ymd.split("-");
  return y && m && d ? `${d}/${m}/${y}` : ymd;
};

type ScheduleGroup = {
  key: string;
  name: string;
  category: string | null;
  items: ScheduleRow[];
  days: number[];
};

function groupSchedules(list: ScheduleRow[]): ScheduleGroup[] {
  const map = new Map<string, ScheduleGroup>();
  for (const s of list) {
    const key = `${(s.name ?? "").trim().toLowerCase()}|${(s.category ?? "").trim().toLowerCase()}|${s.start_time}|${s.end_time}`;
    let g = map.get(key);
    if (!g) {
      g = { key, name: s.name, category: s.category, items: [], days: [] };
      map.set(key, g);
    }
    g.items.push(s);
    if (!g.days.includes(s.day_of_week)) g.days.push(s.day_of_week);
  }
  for (const g of map.values()) g.days.sort();
  return Array.from(map.values());
}




function ListaChamadaPage() {
  const listSchedules = useServerFn(listSchedulesForAttendance);
  const fetchAttendance = useServerFn(getAttendance);
  const persist = useServerFn(saveAttendance);
  const listStudentsFn = useServerFn(listStudentsForAttendance);
  const fetchStudentHistory = useServerFn(getStudentAttendanceHistory);

  const [schedules, setSchedules] = useState<ScheduleRow[]>([]);
  const [groupKey, setGroupKey] = useState<string>("");
  const [date, setDate] = useState<string>(() =>
    brToday(),
  );
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingRows, setLoadingRows] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [studentQuery, setStudentQuery] = useState("");
  const [monthFilter, setMonthFilter] = useState<string>(() => brToday().slice(0, 7));
  const [ackDayMismatch, setAckDayMismatch] = useState(false);




  // Histórico por aluno
  const [studentList, setStudentList] = useState<{ id: string; name: string }[]>([]);
  const [historyStudentId, setHistoryStudentId] = useState<string>("");
  const [historyScope, setHistoryScope] = useState<"month" | "day">("month");
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [completePrompt, setCompletePrompt] = useState(false);
  const [pendingCategory, setPendingCategory] = useState<string | null>(null);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const cancelLeave = () => setLeaveOpen(false);
  const confirmLeave = () => { setDirty(false); setLeaveOpen(false); };


  const groups = useMemo(() => groupSchedules(schedules), [schedules]);


  useEffect(() => {
    (async () => {
      try {
        const res = await listSchedules();
        const list = (res.schedules ?? []) as ScheduleRow[];
        setSchedules(list);
      } catch (e: any) {
        toast.error(e?.message ?? "Erro ao carregar turmas");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedGroup = useMemo(
    () => groups.find((g) => g.key === groupKey) ?? null,
    [groups, groupKey],
  );

  // Ensure a selection exists whenever the (filtered) group list changes
  const categories = useMemo(() => {
    const set = new Set<string>();
    schedules.forEach((s) => s.category && set.add(s.category));
    return Array.from(set).sort();
  }, [schedules]);

  const filteredGroups = useMemo(
    () =>
      groups.filter(
        (g) => categoryFilter === "all" || (g.category ?? "") === categoryFilter,
      ),
    [groups, categoryFilter],
  );

  useEffect(() => {
    if (!filteredGroups.length) {
      if (groupKey) setGroupKey("");
      return;
    }
    if (!filteredGroups.some((g) => g.key === groupKey)) {
      setGroupKey(filteredGroups[0].key);
    }
  }, [filteredGroups, groupKey]);

  const loadRows = () => {
    if (!selectedGroup || !date) return;
    setLoadingRows(true);
    fetchAttendance({
      data: { schedule_ids: selectedGroup.items.map((i) => i.id), class_date: date },
    })
      .then((r) => { setRows((r.students ?? []) as Row[]); setDirty(false); })
      .catch((e: any) => toast.error(e?.message ?? "Erro ao carregar chamada"))
      .finally(() => setLoadingRows(false));
  };


  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup, date]);

  // Carrega lista de alunos para o filtro de histórico
  useEffect(() => {
    (async () => {
      try {
        const r = await listStudentsFn();
        setStudentList((r.students ?? []) as { id: string; name: string }[]);
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Busca histórico quando aluno/mês/dia mudam
  useEffect(() => {
    if (!historyStudentId) { setHistory([]); return; }
    setLoadingHistory(true);
    fetchStudentHistory({
      data: {
        student_id: historyStudentId,
        month: historyScope === "month" ? monthFilter : null,
        date: historyScope === "day" ? date : null,
      },
    })
      .then((r) => setHistory((r.entries ?? []) as HistoryEntry[]))
      .catch((e: any) => toast.error(e?.message ?? "Erro ao carregar histórico"))
      .finally(() => setLoadingHistory(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyStudentId, historyScope, monthFilter, date]);

  const setRow = (id: string, patch: Partial<Row>) => {
    setDirty(true);
    setRows((rs) => {
      const next = rs.map((r) => (r.id === id ? { ...r, ...patch } : r));
      if (next.length && next.every((r) => r.present !== null)) setCompletePrompt(true);
      return next;
    });
  };


  // Day-of-week mismatch detection (parse date as local to avoid TZ shift)
  const dayMismatch = useMemo(() => {
    if (!selectedGroup || !date) return null;
    const [y, m, d] = date.split("-").map(Number);
    if (!y || !m || !d) return null;
    const dow = new Date(y, m - 1, d).getDay();
    if (selectedGroup.days.includes(dow)) return null;
    return {
      picked: DAYS[dow],
      expected: selectedGroup.days.map((x) => DAYS[x]).join(" e "),
    };
  }, [selectedGroup, date]);

  const save = async () => {
    if (!selectedGroup) return;
    const entries = rows
      .filter((r) => r.present !== null)
      .map((r) => ({ student_id: r.id, present: r.present as boolean, notes: r.notes }));
    if (!entries.length) {
      toast.error("Marque ao menos um aluno como presente ou ausente.");
      return;
    }
    // Pick the schedule row whose day_of_week matches the picked date; fallback to first
    const [y, m, d] = date.split("-").map(Number);
    const dow = new Date(y, m - 1, d).getDay();
    const target =
      selectedGroup.items.find((s) => s.day_of_week === dow) ?? selectedGroup.items[0];
    setSaving(true);
    try {
      await persist({
        data: { schedule_id: target.id, class_date: date, entries },
      });
      toast.success("Chamada salva com sucesso!");
      setDirty(false);
      loadRows();
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao salvar chamada");
    } finally {
      setSaving(false);
    }
  };

  const scheduleOptions = useMemo(
    () =>
      filteredGroups.map((g) => ({
        id: g.key,
        label: `${g.name}${g.category ? ` [${g.category}]` : ""} - ${g.days.map((x) => DAYS_SHORT[x]).join(" e ")} (${fmtTime(g.items[0].start_time)} às ${fmtTime(g.items[0].end_time)})`,
      })),
    [filteredGroups],
  );

  const statsGroups = useMemo(
    () =>
      groups.map((g) => ({
        key: g.key,
        label: `${g.name}${g.category ? ` [${g.category}]` : ""}`,
        ids: g.items.map((i) => i.id),
      })),
    [groups],
  );

  // Aviso ao sair da tela com chamada não salva
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  useBlocker({
    shouldBlockFn: () => {
      if (!dirty) return false;
      setLeaveOpen(true);
      return true;
    },
    enableBeforeUnload: false,
  });

  // Reset acknowledgment whenever date/group changes
  useEffect(() => {
    setAckDayMismatch(false);
  }, [date, groupKey]);

  // Sync date within selected month filter
  useEffect(() => {
    if (!monthFilter) return;
    if (!date.startsWith(monthFilter)) {
      setDate(`${monthFilter}-01`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthFilter]);




  const visibleRows = useMemo(() => {
    const q = studentQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r.name ?? "").toLowerCase().includes(q));
  }, [rows, studentQuery]);

  const blockSave = !!dayMismatch && !ackDayMismatch;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <ClipboardCheck className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Lista de Chamada</h1>
          <p className="text-sm text-muted-foreground">
            Registre presenças e faltas dos alunos por horário.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtros de Aula</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-2">
                  <Label htmlFor="month">Mês</Label>
                  <Input
                    id="month"
                    type="month"
                    value={monthFilter}
                    onChange={(e) => setMonthFilter(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date">Data da Aula</Label>
                  <Input
                    id="date"
                    type="date"
                    value={date}
                    min={monthFilter ? `${monthFilter}-01` : undefined}
                    max={monthFilter ? `${monthFilter}-31` : undefined}
                    onChange={(e) => setDate(e.target.value)}
                    className="border-2 border-emerald-600 focus-visible:ring-emerald-600"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Categoria</Label>
                  <Select
                    value={categoryFilter}
                    onValueChange={(v) => {
                      if (v !== "all" && selectedGroup) setPendingCategory(v);
                      else setCategoryFilter(v);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas as categorias</SelectItem>
                      {categories.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Horário / Turma</Label>
                  <Select value={groupKey} onValueChange={setGroupKey}>
                    <SelectTrigger className="bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700 focus:ring-emerald-600 [&>svg]:text-white data-[placeholder]:text-white/80">
                      <SelectValue placeholder="Selecione uma turma" />
                    </SelectTrigger>
                    <SelectContent>
                      {scheduleOptions.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-muted-foreground">
                          Nenhuma turma disponível.
                        </div>
                      ) : (
                        scheduleOptions.map((o) => (
                          <SelectItem key={o.id} value={o.id}>
                            {o.label}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>




              {dayMismatch ? (
                <Alert variant="destructive" className="border-amber-500/50 bg-amber-50 text-amber-900 [&>svg]:text-amber-600 dark:bg-amber-950/30 dark:text-amber-200">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Data em dia diferente do previsto</AlertTitle>
                  <AlertDescription className="space-y-2">
                    <p>
                      Esta turma acontece normalmente às <strong>{dayMismatch.expected}</strong>, mas você selecionou uma <strong>{dayMismatch.picked}</strong>. Deseja continuar assim mesmo?
                    </p>
                    <div className="flex gap-2 pt-1">
                      <Button
                        type="button"
                        size="sm"
                        variant={ackDayMismatch ? "default" : "outline"}
                        onClick={() => setAckDayMismatch(true)}
                        disabled={ackDayMismatch}
                      >
                        {ackDayMismatch ? "Confirmado — pode salvar" : "Sim, continuar"}
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
              ) : null}

              <div className="pt-4 border-t space-y-3">
                <div>
                  <Label className="text-sm font-semibold">Histórico por aluno</Label>
                  <p className="text-xs text-muted-foreground">
                    Escolha um aluno para ver todas as presenças e faltas dele no mês ou no dia selecionado acima.
                  </p>
                </div>
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="space-y-2 md:col-span-2">
                    <Label>Aluno</Label>
                    <Select value={historyStudentId || "none"} onValueChange={(v) => setHistoryStudentId(v === "none" ? "" : v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um aluno" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Nenhum —</SelectItem>
                        {studentList.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Período</Label>
                    <Select value={historyScope} onValueChange={(v) => setHistoryScope(v as "month" | "day")}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="month">Mês inteiro ({monthFilter})</SelectItem>
                        <SelectItem value="day">Somente o dia ({fmtDateBR(date)})</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {historyStudentId ? (
                  <div className="rounded-lg border bg-muted/30 p-3">
                    {loadingHistory ? (
                      <div className="flex justify-center py-4">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                    ) : history.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-2">
                        Nenhum registro encontrado para o período.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                          <span>Total: <strong className="text-foreground">{history.length}</strong></span>
                          <span className="text-emerald-700 dark:text-emerald-400">
                            Presenças: <strong>{history.filter((h) => h.present).length}</strong>
                          </span>
                          <span className="text-rose-700 dark:text-rose-400">
                            Faltas: <strong>{history.filter((h) => !h.present).length}</strong>
                          </span>
                        </div>
                        <div className="space-y-1 max-h-72 overflow-auto">
                          {history.map((h, i) => (
                            <div key={i} className="flex items-center justify-between gap-2 rounded-md border bg-card px-3 py-2 text-sm">
                              <div className="min-w-0">
                                <div className="font-medium truncate">
                                  {fmtDateBR(h.class_date)} — {h.schedule_name}
                                  {h.schedule_category ? ` [${h.schedule_category}]` : ""}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {h.start_time ? `${fmtTime(h.start_time)}${h.end_time ? " às " + fmtTime(h.end_time) : ""}` : ""}
                                  {h.marked_at ? ` · Registrado em ${fmtDateTime(h.marked_at)}` : ""}
                                </div>
                              </div>
                              <span
                                className={`text-xs font-semibold px-2 py-1 rounded shrink-0 ${
                                  h.present
                                    ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
                                    : "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200"
                                }`}
                              >
                                {h.present ? "Presente" : "Falta"}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <CardTitle>Alunos da Turma</CardTitle>
            {rows.some((r) => r.present !== null) && (
              <span className="text-xs rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 px-2 py-1 font-semibold">
                Índice de presença: {(() => {
                  const marked = rows.filter((r) => r.present !== null);
                  return `${Math.round((marked.filter((r) => r.present).length / marked.length) * 100)}%`;
                })()}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setHistoryOpen(true)}>
              <History className="mr-1 h-4 w-4" /> Histórico por aluno
            </Button>
            <Button variant="outline" size="sm" onClick={() => setStatsOpen(true)}>
              <BarChart3 className="mr-1 h-4 w-4" /> Estatísticas
            </Button>
            <Button onClick={save} disabled={saving || !rows.length || blockSave} title={blockSave ? "Confirme o aviso de data acima" : undefined}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Salvar Chamada
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingRows ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-10 border-2 border-dashed rounded-xl">
              <p className="text-muted-foreground">
                {groupKey
                  ? "Nenhum aluno matriculado nesta turma."
                  : "Selecione uma turma para visualizar os alunos."}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Filtrar aluno pelo nome..."
                  value={studentQuery}
                  onChange={(e) => setStudentQuery(e.target.value)}
                  className="pl-8"
                />
              </div>
              {visibleRows.length === 0 ? (
                <div className="text-center py-6 text-sm text-muted-foreground border border-dashed rounded-lg">
                  Nenhum aluno corresponde ao filtro.
                </div>
              ) : (
                <div className="space-y-2">
                  {visibleRows.map((r, idx) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between gap-3 rounded-lg border bg-card px-3 py-2"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-sm font-semibold text-muted-foreground w-6 text-right">
                      {idx + 1}.
                    </span>
                    <Avatar className="h-9 w-9">
                      {r.photo_url ? <AvatarImage src={r.photo_url} alt={r.name} /> : null}
                      <AvatarFallback>{(r.name ?? "?").slice(0, 1).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{r.name}</div>
                      {r.jersey_number ? (
                        <div className="text-xs text-muted-foreground">Nº {r.jersey_number}</div>
                      ) : null}
                    </div>
                    {selectedGroup ? (
                      <ChangeClassButton
                        studentId={r.id}
                        studentName={r.name}
                        currentClassLabel={selectedGroup.name}
                        currentClassKey={selectedGroup.key}
                        currentScheduleIds={selectedGroup.items.map((i) => i.id)}
                        classes={groups.map((g) => ({
                          key: g.key,
                          label: `${g.name}${g.category ? ` · ${g.category}` : ""} · ${g.days.map((d) => DAYS_SHORT[d]).join(", ")} ${fmtTime(g.items[0]?.start_time ?? "")}`,
                          scheduleIds: g.items.map((i) => i.id),
                        }))}
                        onChanged={loadRows}
                      />
                    ) : null}
                  </div>

                  <div className="flex items-center gap-3 shrink-0">
                    {r.marked_at && r.present !== null ? (
                      <span className="hidden md:inline text-[11px] text-muted-foreground whitespace-nowrap">
                        Registrado em {fmtDateTime(r.marked_at)}
                      </span>
                    ) : null}
                    <div className="flex flex-col items-end gap-1">
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant={r.present === true ? "default" : "outline"}
                          className={
                            r.present === true
                              ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                              : ""
                          }
                          onClick={() => setRow(r.id, { present: r.present === true ? null : true })}
                        >
                          <UserCheck className="mr-1 h-4 w-4" /> Presente
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={r.present === false ? "default" : "outline"}
                          className={
                            r.present === false
                              ? "bg-rose-600 hover:bg-rose-700 text-white"
                              : ""
                          }
                          onClick={() => setRow(r.id, { present: r.present === false ? null : false })}
                        >
                          <UserX className="mr-1 h-4 w-4" /> Falta
                        </Button>
                      </div>
                      {r.marked_at && r.present !== null ? (
                        <span className="md:hidden text-[10px] text-muted-foreground">
                          {fmtDateTime(r.marked_at)}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Histórico por aluno */}

      <Dialog open={historyOpen} onOpenChange={(v) => !v && setHistoryOpen(false)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Histórico por aluno</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Escolha um aluno para ver todas as presenças e faltas dele no mês ou no dia selecionado na tela.
          </p>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="space-y-2 md:col-span-2">
              <Label>Aluno</Label>
              <Select value={historyStudentId || "none"} onValueChange={(v) => setHistoryStudentId(v === "none" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um aluno" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Nenhum —</SelectItem>
                  {studentList.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Período</Label>
              <Select value={historyScope} onValueChange={(v) => setHistoryScope(v as "month" | "day")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Mês inteiro ({monthFilter})</SelectItem>
                  <SelectItem value="day">Somente o dia ({fmtDateBR(date)})</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {historyStudentId ? (
            <div className="rounded-lg border bg-muted/30 p-3">
              {loadingHistory ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              ) : history.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-2">
                  Nenhum registro encontrado para o período.
                </p>
              ) : (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                    <span>Total: <strong className="text-foreground">{history.length}</strong></span>
                    <span className="text-emerald-700 dark:text-emerald-400">
                      Presenças: <strong>{history.filter((h) => h.present).length}</strong>
                    </span>
                    <span className="text-rose-700 dark:text-rose-400">
                      Faltas: <strong>{history.filter((h) => !h.present).length}</strong>
                    </span>
                  </div>
                  <div className="space-y-1 max-h-72 overflow-auto">
                    {history.map((h, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 rounded-md border bg-card px-3 py-2 text-sm">
                        <div className="min-w-0">
                          <div className="font-medium truncate">
                            {fmtDateBR(h.class_date)} — {h.schedule_name}
                            {h.schedule_category ? ` [${h.schedule_category}]` : ""}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {h.start_time ? `${fmtTime(h.start_time)}${h.end_time ? " às " + fmtTime(h.end_time) : ""}` : ""}
                            {h.marked_at ? ` · Registrado em ${fmtDateTime(h.marked_at)}` : ""}
                          </div>
                        </div>
                        <span
                          className={`text-xs font-semibold px-2 py-1 rounded shrink-0 ${
                            h.present
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
                              : "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200"
                          }`}
                        >
                          {h.present ? "Presente" : "Falta"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Estatísticas gerais */}
      <AttendanceStatsDialog
        open={statsOpen}
        onClose={() => setStatsOpen(false)}
        groups={statsGroups}
        defaultGroupKey={groupKey}
      />

      {/* Chamada concluída — deseja salvar? */}
      <AlertDialog open={completePrompt} onOpenChange={(v) => !v && setCompletePrompt(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>CHAMADA CONCLUÍDA</AlertDialogTitle>
            <AlertDialogDescription>
              Todos os alunos desta turma já foram marcados. Deseja salvar a chamada agora?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCompletePrompt(false)}>Agora não</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setCompletePrompt(false); save(); }} disabled={blockSave}>
              Salvar chamada
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Aviso do filtro de categoria */}
      <AlertDialog open={!!pendingCategory} onOpenChange={(v) => !v && setPendingCategory(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ATENÇÃO: ALUNOS PODEM SER OCULTADOS</AlertDialogTitle>
            <AlertDialogDescription>
              A turma selecionada pode ter alunos de categorias diferentes. Ao aplicar o filtro por categoria,
              ALUNOS DA MESMA TURMA MAS DE CATEGORIAS DIFERENTES NÃO SERÃO EXIBIDOS. Deseja continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingCategory(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setCategoryFilter(pendingCategory as string); setPendingCategory(null); }}>
              Continuar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Saída sem salvar */}
      <AlertDialog open={leaveOpen} onOpenChange={(v) => !v && cancelLeave()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Você está saindo da lista de chamada</AlertDialogTitle>
            <AlertDialogDescription>
              Foram feitas alterações que ainda não foram salvas. É necessário salvar para não perder os registros.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel onClick={cancelLeave}>Continuar aqui</AlertDialogCancel>
            <Button variant="outline" onClick={confirmLeave}>Sair sem salvar</Button>
            <AlertDialogAction
              onClick={async () => { await save(); confirmLeave(); }}
              disabled={blockSave || saving}
            >
              Salvar e sair
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

